/**
 * Noticing a newer release while the app is open.
 *
 * A tab keeps running the build it loaded until it does a full page load, and `chunkReload.ts`
 * only catches up when the tab happens to need a chunk the deploy removed. A tab that stays on
 * pages it has already loaded can run an old build for days, against an API that has moved on.
 * So each build writes its id to `/version.json` (vite.config.mts) and an open tab compares it
 * with its own.
 */

export const BUILD_VERSION_URL = '/version.json';
export const BUILD_CHECK_INTERVAL_MS = 5 * 60_000;

/** The id of the build being served now, or null when it can't be read (offline, mid-deploy). */
export const fetchLiveBuildId = async (): Promise<string | null> => {
    try {
        const response = await fetch(BUILD_VERSION_URL, { cache: 'no-store' });
        if (!response.ok) return null;
        const body: unknown = await response.json();
        if (body == null || typeof body !== 'object' || !('buildId' in body)) return null;
        return typeof body.buildId === 'string' && body.buildId !== '' ? body.buildId : null;
    } catch {
        return null;
    }
};

type WatchForNewBuildOptions = {
    currentBuildId: string;
    onNewBuild: () => void;
    fetchLive?: () => Promise<string | null>;
    intervalMs?: number;
};

/**
 * Checks every `intervalMs` while the tab is visible, and as soon as it becomes visible again (a
 * laptop waking up, a tab restored the next morning). Calls `onNewBuild` once, the first time the
 * live build differs, then stops. A failed read is not a new build. Returns a stop function.
 */
export const watchForNewBuild = ({
    currentBuildId,
    onNewBuild,
    fetchLive = fetchLiveBuildId,
    intervalMs = BUILD_CHECK_INTERVAL_MS,
}: WatchForNewBuildOptions) => {
    const listeners = new AbortController();
    let timer: number | undefined;
    let stopped = false;
    let checking = false;

    const stop = () => {
        stopped = true;
        window.clearInterval(timer);
        listeners.abort();
    };

    const check = async () => {
        if (stopped || checking || document.visibilityState === 'hidden') return;
        checking = true;
        const liveBuildId = await fetchLive();
        checking = false;
        if (stopped || liveBuildId == null || liveBuildId === currentBuildId) return;
        stop();
        onNewBuild();
    };

    timer = window.setInterval(() => {
        check();
    }, intervalMs);
    document.addEventListener(
        'visibilitychange',
        () => {
            check();
        },
        { signal: listeners.signal },
    );

    return stop;
};
